import { useEffect, useMemo, useState } from "react";
import { works } from "@/mocks/works";
import WorkCard from "@/components/feature/WorkCard";

interface Props {
  keyword: string;
  onClearKeyword: () => void;
}

type SortKey = "heat" | "updated" | "rating";

export default function WorksGallery({ keyword, onClearKeyword }: Props) {
  const [cat, setCat] = useState("all");
  const [sort, setSort] = useState<SortKey>("heat");
  const [visible, setVisible] = useState(8);
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
    if (keyword) {
      document.getElementById("gallery")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [keyword]);

  const result = useMemo(() => {
    let list = works;
    if (cat !== "all") list = list.filter((w) => w.categorySlug === cat);
    if (input.trim()) {
      const kw = input.trim().toLowerCase();
      list = list.filter(
        (w) =>
          w.title.toLowerCase().includes(kw) ||
          w.summary.toLowerCase().includes(kw) ||
          w.tags.some((t) => t.toLowerCase().includes(kw)),
      );
    }
    const sorted = [...list];
    if (sort === "heat") sorted.sort((a, b) => b.heat - a.heat);
    else if (sort === "updated") sorted.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    else sorted.sort((a, b) => b.rating - a.rating);
    return sorted;
  }, [cat, sort, input]);

  const shown = result.slice(0, visible);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    onClearKeyword();
  };

  return (
    <section id="gallery" className="container-x py-14 md:py-20" aria-labelledby="gallery-heading">
      <div className="flex items-end justify-between gap-4">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-primary-600">
            作品总览
          </span>
          <h2 id="gallery-heading" className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            全部作品
          </h2>
        </div>
        <span className="hidden text-sm text-foreground-500 md:inline">
          共 {result.length} 部
        </span>
      </div>

      <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {["all", ...["chuanyue", "xitong", "zhongsheng", "richang", "zhandou"]].map((c) => {
            const label =
              c === "all" ? "全部" : { chuanyue: "穿越", xitong: "系统", zhongsheng: "重生", richang: "日常", zhandou: "战斗" }[c];
            return (
              <button
                key={c}
                type="button"
                onClick={() => setCat(c)}
                className={`rounded-full px-3.5 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
                  cat === c
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <form onSubmit={handleSearch} className="flex items-center gap-2" role="search">
            <label className="sr-only" htmlFor="gallery-search">
              搜索
            </label>
            <input
              id="gallery-search"
              type="search"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="搜索作品标题或标签…"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 sm:w-56"
            />
            <button
              type="submit"
              className="inline-flex items-center justify-center rounded-md bg-background-100 px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-200"
            >
              <i className="ri-search-line" />
            </button>
          </form>

          <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
            {(
              [
                ["heat", "热度"],
                ["rating", "评分"],
                ["updated", "更新"],
              ] as [SortKey, string][]
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setSort(key)}
                className={`rounded-full px-3 py-1 text-sm whitespace-nowrap transition-colors ${
                  sort === key
                    ? "bg-background-50 text-primary-700 font-medium"
                    : "text-foreground-500 hover:text-foreground-800"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {shown.length > 0 ? (
        <>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {shown.map((w) => (
              <WorkCard key={w.id} work={w} />
            ))}
          </div>
          {result.length > visible && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + 8)}
                className="inline-flex items-center gap-2 rounded-md border border-background-300 px-6 py-2.5 text-sm font-medium whitespace-nowrap text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                加载更多 <i className="ri-arrow-down-line" />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
          <i className="ri-search-eye-line text-4xl text-background-300" />
          <p className="mt-4 text-sm text-foreground-600">没有找到匹配的作品</p>
          <button
            type="button"
            onClick={() => {
              setInput("");
              setCat("all");
              onClearKeyword();
            }}
            className="mt-4 rounded-md bg-primary-500 px-5 py-2 text-sm font-medium whitespace-nowrap text-background-50 hover:bg-primary-600"
          >
            清除筛选
          </button>
        </div>
      )}
    </section>
  );
}